import { useEffect, useId, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Banknote, Bike, ChevronRight, MapPin, Search, ShoppingBag, UserRound } from "lucide-react";
import { formatWeight, isSoldByWeight } from "@tarawa/shared";
import { BlurFade, DotPattern, cn } from "@tarawa/ui";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useHome } from "@/lib/catalog";
import { useI18n } from "@/lib/i18n";
import type { Category, Product } from "@/lib/types";
import { Logo } from "@/components/Logo";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { ProductCard, ProductGrid, ProductGridSkeleton, ProductImage, type ProductLinkState } from "@/components/product";
import { PackCard } from "@/components/pack";
import { Alert, ErrorState } from "@/components/ui";

/** Minimum characters before live suggestions are fetched */
const SUGGEST_MIN = 2;
const SUGGEST_LIMIT = 6;

export function SearchBar({ initial = "", autoFocus }: { initial?: string; autoFocus?: boolean }) {
  const { t, ln, money, locale } = useI18n();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [q, setQ] = useState(initial);
  // Suggestions open only once the customer types (not for the query a results page starts with).
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const term = q.trim();
  const [debounced, setDebounced] = useState(term);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(term), 200);
    return () => clearTimeout(id);
  }, [term]);
  const enabled = open && debounced.length >= SUGGEST_MIN;
  // Same search as the results page, limited to products available for sale.
  const { data: suggestions, isPlaceholderData } = useQuery({
    queryKey: ["catalog", "suggest", debounced],
    queryFn: () =>
      api<{ products: Product[] }>(`/catalog/products?q=${encodeURIComponent(debounced)}&limit=${SUGGEST_LIMIT}&available=true`).then((r) => r.products),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const listId = useId();
  const settled = !isPlaceholderData && debounced === term;
  // While typing, the previous suggestions stay visible; "no results" only once this query is answered.
  const showList = enabled && term.length >= SUGGEST_MIN && suggestions !== undefined && (suggestions.length > 0 || settled);
  const items = showList ? suggestions : [];

  const close = () => {
    setOpen(false);
    setActive(-1);
  };
  const goTo = (p: Product) => {
    close();
    const state: ProductLinkState = { from: pathname + search };
    navigate(`/products/${p.id}`, { state });
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (active >= 0 && items[active]) return goTo(items[active]);
    close();
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") return close();
    if (!items.length || (e.key !== "ArrowDown" && e.key !== "ArrowUp")) return;
    e.preventDefault();
    setActive((i) => (e.key === "ArrowDown" ? (i + 1) % items.length : (i <= 0 ? items.length : i) - 1));
  };

  return (
    <form onSubmit={submit} role="search" className="relative">
      <Search className="pointer-events-none absolute start-4 top-1/2 size-5 -translate-y-1/2 text-subtle" />
      <input
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => q !== initial && setOpen(true)}
        onBlur={close}
        onKeyDown={onKeyDown}
        placeholder={t("home.searchPlaceholder")}
        aria-label={t("home.searchPlaceholder")}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        autoFocus={autoFocus}
        enterKeyHint="search"
        maxLength={80}
        className="h-13 w-full rounded-2xl border border-line bg-surface ps-12 pe-4 text-[16px] shadow-[var(--shadow-card)] placeholder:text-subtle focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 focus:outline-none"
      />
      {showList && (
        <div className="absolute inset-x-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-line bg-surface shadow-[var(--shadow-float)]">
          {items.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted">{t("search.noResults", { q: term })}</p>
          ) : (
            <ul id={listId} role="listbox" aria-label={t("home.searchPlaceholder")} className="max-h-[60vh] overflow-y-auto py-1">
              {items.map((p, i) => {
                const name = ln(p.name, p.nameAr);
                const byWeight = isSoldByWeight({ ...p, baseWeightGrams: p.baseWeightGrams ?? null, weightOptionsGrams: p.weightOptionsGrams ?? [] });
                return (
                  <li
                    key={p.id}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    // Keep focus in the input so the click is not lost to its blur.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => goTo(p)}
                    onMouseEnter={() => setActive(i)}
                    className={cn("flex cursor-pointer items-center gap-3 px-3 py-2", i === active && "bg-brand-50")}
                  >
                    <ProductImage src={p.imageUrl} alt={name} seed={p.id} className="size-11 shrink-0 rounded-xl" />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">{name}</span>
                    <span className="ltr-nums shrink-0 text-end text-sm font-bold text-brand-800">
                      {money(p.priceCents)}
                      {byWeight && <span className="block text-[11px] leading-3 font-semibold text-muted">/ {formatWeight(p.baseWeightGrams!, locale)}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </form>
  );
}

export function CategoryTile({ category, className }: { category: Category; className?: string }) {
  const { ln } = useI18n();
  const name = ln(category.name, category.nameAr);
  return (
    <Link to={`/categories/${category.slug}`} className={cn("group flex flex-col items-center gap-2 text-center", className)}>
      <div className="size-[72px] overflow-hidden rounded-[22px] bg-surface shadow-[var(--shadow-card)] transition group-hover:shadow-lg md:size-20">
        <ProductImage src={category.imageUrl} alt={name} seed={category.slug} className="size-full" />
      </div>
      <span className="line-clamp-2 text-xs leading-4 font-semibold text-ink">{name}</span>
    </Link>
  );
}

function SectionTitle({ title, to }: { title: string; to?: string }) {
  const { t } = useI18n();
  return (
    <div className="mb-3 flex items-end justify-between">
      <h2 className="text-lg font-bold tracking-tight md:text-xl">{title}</h2>
      {to && (
        <Link to={to} className="flex items-center gap-0.5 text-sm font-semibold text-brand-700">
          {t("common.seeAll")}
          <ChevronRight className="size-4 rtl:rotate-180" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const { t, ln, money, locale } = useI18n();
  const { customer } = useAuth();
  const { count } = useCart();
  const { data, isLoading, isError, refetch } = useHome();
  const settings = data?.settings;
  const banner = settings ? (locale === "ar" ? settings.promoBannerAr || settings.promoBannerFr : settings.promoBannerFr) : null;
  const heroTitle = settings ? (locale === "ar" ? settings.heroTitleAr || settings.heroTitleFr : settings.heroTitleFr) : null;
  const badges = settings?.heroBadges ?? ["COD", "FEE", "MIN"];

  return (
    <div className="space-y-7">
      {/* Mobile top bar: logo, language, account, cart */}
      <div className="flex items-center justify-between md:hidden">
        <Logo />
        <div className="flex items-center gap-2">
          <LanguageSwitch />
          <Link to="/account" className="flex size-10 items-center justify-center rounded-full bg-surface shadow-[var(--shadow-card)]" aria-label={t("nav.account")}>
            <UserRound className="size-5 text-ink" />
          </Link>
          <Link to="/cart" className="relative flex size-10 items-center justify-center rounded-full bg-brand-600 text-white shadow-[var(--shadow-float)]" aria-label={t("nav.cart")}>
            <ShoppingBag className="size-5" />
            {count > 0 && (
              <span className="ltr-nums absolute -end-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-saffron-400 px-1 text-[11px] font-bold text-ink">
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-sm text-muted">{customer ? t("home.hello", { name: customer.fullName.split(" ")[0]! }) : t("home.helloGuest")} 👋</p>
          {settings && (
            <p className="mt-0.5 flex flex-wrap items-center gap-1 font-bold text-ink">
              <MapPin className="size-4 text-brand-600" />
              {t("home.deliveryTo", { city: ln(settings.deliveryCity, settings.deliveryCityAr) })}
              {settings.deliveryStartTime && settings.deliveryEndTime && (
                <span className="ltr-nums font-semibold text-muted">
                  · {t("home.deliveryHours", { start: settings.deliveryStartTime, end: settings.deliveryEndTime })}
                </span>
              )}
            </p>
          )}
        </div>
        <SearchBar />
      </div>

      {settings && !settings.deliveryEnabled && <Alert>{t("home.deliveryOff")}</Alert>}
      {settings?.deliveryEnabled && settings.ordering && settings.ordering.state !== "OPEN" && (
        <Alert>
          <span className="ltr-nums">{t("cart.orderingClosed", { start: settings.ordering.opensAt ?? "", end: settings.ordering.closesAt ?? "" })}</span>
        </Alert>
      )}

      {/* Promotional banner */}
      <BlurFade>
        <section className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 p-5 text-white shadow-[var(--shadow-float)] md:p-8">
          <DotPattern width={18} height={18} cr={1.2} className="text-white/15 [mask-image:radial-gradient(260px_circle_at_85%_20%,white,transparent)]" />
          <div className="absolute -end-10 -bottom-14 size-44 rounded-full bg-saffron-400/25 blur-2xl" aria-hidden="true" />
          <div className="relative max-w-md">
            <h1 className="text-2xl leading-tight font-extrabold md:text-3xl">{heroTitle || t("home.heroTitle")}</h1>
            <p className="mt-1.5 text-sm text-white/85 md:text-base">{banner || t("home.heroText")}</p>
            {/* Badges chosen and ordered by the Admin (store settings) */}
            <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
              {badges.map((b) =>
                b === "COD" ? (
                  <span key={b} className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 backdrop-blur">
                    <Banknote className="size-3.5" />
                    {t("home.codBadge")}
                  </span>
                ) : b === "FEE" && settings ? (
                  <span key={b} className="ltr-nums inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-brand-800">
                    <Bike className="size-3.5" />
                    {settings.deliveryFeeCents > 0 ? t("home.fee", { amount: money(settings.deliveryFeeCents) }) : t("home.freeDelivery")}
                  </span>
                ) : b === "MIN" && settings && settings.minOrderCents > 0 ? (
                  <span key={b} className="ltr-nums inline-flex items-center rounded-full bg-white/15 px-3 py-1.5">
                    {t("home.minOrder", { amount: money(settings.minOrderCents) })}
                  </span>
                ) : null,
              )}
            </div>
          </div>
        </section>
      </BlurFade>

      {isError && !data ? (
        <ErrorState onRetry={() => refetch()} />
      ) : (
        <>
          <section>
            <SectionTitle title={t("home.categories")} to="/categories" />
            {isLoading ? (
              <div className="flex gap-4 overflow-hidden">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="size-[72px] shrink-0 animate-pulse rounded-[22px] bg-surface" />
                ))}
              </div>
            ) : (
              <div className="no-scrollbar -mx-4 flex gap-4 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-6 md:px-0 lg:grid-cols-8">
                {data?.categories.map((c) => (
                  <CategoryTile key={c.id} category={c} className="w-[76px] shrink-0 md:w-auto" />
                ))}
              </div>
            )}
          </section>

          {(data?.packs?.length ?? 0) > 0 && (
            <BlurFade inView>
              <section>
                <SectionTitle title={t("home.packs")} />
                <div className="no-scrollbar -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:px-0 md:gap-4 lg:grid-cols-4">
                  {data?.packs?.map((p) => (
                    <PackCard key={p.id} pack={p} className="w-[62%] shrink-0 snap-start sm:w-auto" />
                  ))}
                </div>
              </section>
            </BlurFade>
          )}

          {/* Seasonal products chosen by the Admin; hidden when none is available */}
          {(data?.seasonal?.length ?? 0) > 0 && (
            <BlurFade inView>
              <section>
                <SectionTitle title={t("home.seasonal")} />
                <div className="no-scrollbar -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:px-0 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
                  {data?.seasonal?.map((p) => (
                    <div key={p.id} className="w-[46%] shrink-0 snap-start sm:w-auto">
                      <ProductCard product={p} />
                    </div>
                  ))}
                </div>
              </section>
            </BlurFade>
          )}

          {(isLoading || (data?.featured.length ?? 0) > 0) && (
            <BlurFade inView>
              <section>
                <SectionTitle title={t("home.featured")} />
                {isLoading ? (
                  <ProductGridSkeleton count={4} />
                ) : (
                  <div className="no-scrollbar -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:px-0 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
                    {data?.featured.map((p) => (
                      <div key={p.id} className="w-[46%] shrink-0 snap-start sm:w-auto">
                        <ProductCard product={p} />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </BlurFade>
          )}

          {settings?.popularLimit !== 0 && (
            <BlurFade inView>
              <section>
                <SectionTitle title={t("home.popular")} />
                {isLoading ? <ProductGridSkeleton /> : <ProductGrid products={data?.popular ?? []} />}
              </section>
            </BlurFade>
          )}
        </>
      )}
    </div>
  );
}
